import {useContext, useEffect, useMemo, useState} from 'react';

import {useField} from 'formik';
import {useTranslation} from 'react-i18next';

import {Autocomplete, MenuItem, Stack, TextField, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionLabeledValueList} from '../EmpionLabeledValueList/EmpionLabeledValueList';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {ExperienceMetadata} from '../../features/applicant/profile/types';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';
import {YEARS_OF_EXPERIENCE} from '../../lib/constants';
import {RequiredLanguageProfile, TrackRecord} from '../../types/shared-types';
import {formatProfessionalExperienceDisplay} from '../../utils/formatProfessionalExpDisplay';

interface Props {
  name: string;
  options: TrackRecord<ExperienceMetadata>[];
}

export const TrackRecordInput = ({name, options}: Props) => {
  const lang = useContext(LanguageContext);
  const {t} = useTranslation();
  const theme = useTheme();

  const [selectedItems, setSelectedItems] = useState<TrackRecord<ExperienceMetadata>[]>([]);
  const [activeOption, setActiveOption] = useState<TrackRecord<ExperienceMetadata> | null>(null);
  const [yearsOfExperience, setYearsOfExperience] = useState('1_2_years');

  const [field, , helpers] = useField(name);
  useEffect(() => {
    const value = field.value || ([] as TrackRecord<ExperienceMetadata>[]);
    setSelectedItems(value);
    // NOTE: This should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (yearsOfExperience) {
      setActiveOption((opt) => ({
        ...opt,
        name: opt?.name || '',
        metadata: {yearsOfExperience, label: opt?.metadata.label || ''},
      }));
    }
  }, [yearsOfExperience]);

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems(selectedItems.filter((s) => `${s.id}` !== itemId));
  };
  const themedTextField = getFieldStyle(theme);
  const groups = useMemo(() => {
    const topSkillGroup = {
      color: 'secondary' as const,
      items: selectedItems.map((entry) => formatProfessionalExperienceDisplay(entry, lang)),
    };

    return [topSkillGroup];
  }, [lang, selectedItems]);

  useEffect(() => {
    helpers.setValue(selectedItems);
  }, [selectedItems, helpers]);

  const convertedOptions = options.map((entry) => ({
    id: entry.id,
    label: (entry.name as RequiredLanguageProfile)[lang],
  }));

  return (
    <div>
      <Typography variant='h2' gutterBottom>
        {t('shared.formMaker.skills.professionalExperience')}
      </Typography>
      <Typography variant='body1' gutterBottom>
        {t('shared.formMaker.skills.chooseProfessionalExperience')}
      </Typography>
      <Autocomplete
        value={convertedOptions.find((entry) => entry.id === activeOption?.id) || null}
        onChange={(event, newValue) => {
          const value = options.find((entry) => entry.id === newValue?.id);
          if (value) {
            setActiveOption(value);
          }
        }}
        id='combo-box-demo'
        options={convertedOptions}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            size='small'
            fullWidth
            variant='outlined'
            sx={themedTextField}
            data-testid='autocomplete-skill-input'
          />
        )}
      />
      <Stack direction='row' spacing={1} sx={{marginTop: 1}}>
        <TextField
          select
          sx={themedTextField}
          fullWidth
          size='small'
          value={yearsOfExperience}
          onChange={(e) => setYearsOfExperience(e.target.value)}
          variant='outlined'
          data-testid='duration-unit-input'
        >
          {YEARS_OF_EXPERIENCE.map((option) => (
            <MenuItem key={option.id} value={option.id} data-testid={`duration-unit-${option.id}`}>
              {option.label[lang]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <EmpionButton
        sx={{
          mt: 1,
        }}
        fullWidth
        onClick={() => {
          if (activeOption && !selectedItems.find((it) => it.id === activeOption.id)) {
            setSelectedItems([
              ...selectedItems,
              {...activeOption, metadata: {yearsOfExperience, label: activeOption.metadata.label}},
            ]);
          }
        }}
        data-testid='skill-add-button'
      >
        {t('shared.formButtons.add')}
      </EmpionButton>
      <div>
        <EmpionLabeledValueList light title={''} onDelete={(item) => handleRemoveItem(item)} groups={groups} />
      </div>
    </div>
  );
};
